/**
 * CohortKsbView — E2-S4 REVIEW-section view.
 *
 * Consumes GET /analytics/cohort-ksb?orgPath=... from chamber-v2, which
 * returns per-KSB attainment across all attempts of a cohort (org-path
 * prefix), pre-sorted weakest → strongest. Rendered as a horizontal bar
 * chart so a Command Data Officer can see "which KSBs is this cycle
 * weakest on?" in one glance — the E2-S4 accept criterion.
 */

import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import { apiFetch } from '../providers/api-rest-provider';
import { canonicalOrgPath, isOrgPathWithin } from '../hooks/useRollup';

interface KsbEntry {
  tag: string;
  attainment: number;   // 0..100
  sampleSize: number;
  studentCount: number;
}

interface CohortKsbResponse {
  orgPath: string;
  cohortSize: number;
  attempts: number;
  entries: KsbEntry[];
}

function colorForPct(pct: number): 'success' | 'warning' | 'error' {
  if (pct >= 75) return 'success';
  if (pct >= 50) return 'warning';
  return 'error';
}

export function CohortKsbView({ rootOrgPath }: { rootOrgPath: string }) {
  const [orgPath, setOrgPath] = useState(rootOrgPath);
  const queryOrgPath = canonicalOrgPath(orgPath) === orgPath &&
    isOrgPathWithin(rootOrgPath, orgPath) ? orgPath : null;
  const requestKey = queryOrgPath ?? '';
  const [result, setResult] = useState<{
    key: string;
    data: CohortKsbResponse | null;
    error: string | null;
    loading: boolean;
  }>({ key: '', data: null, error: null, loading: false });
  const current = result.key === requestKey
    ? result
    : { data: null, error: null, loading: Boolean(queryOrgPath) };

  useEffect(() => {
    setOrgPath(rootOrgPath);
  }, [rootOrgPath]);

  useEffect(() => {
    if (!queryOrgPath) return;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const encoded = encodeURIComponent(queryOrgPath);
        const raw = await apiFetch(
          `analytics/cohort-ksb?orgPath=${encoded}`,
          { signal: controller.signal },
        ) as CohortKsbResponse;
        if (raw.orgPath !== queryOrgPath) {
          throw new Error('Analytics response scope did not match request');
        }
        if (!controller.signal.aborted) {
          setResult({ key: requestKey, data: raw, error: null, loading: false });
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setResult({
            key: requestKey,
            data: null,
            error: err instanceof Error ? err.message : String(err),
            loading: false,
          });
        }
      }
    }, 300); // debounce
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [queryOrgPath, requestKey]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <BarChartIcon color="primary" />
        <Typography variant="h5">Cohort KSB Attainment</Typography>
        <Chip label="E2-S4" size="small" color="warning" />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Which KSBs is this cycle weakest on? Explore the assigned organization
        or one of its descendants. Table is sorted weakest → strongest.
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <TextField
            label="orgPath"
            fullWidth
            value={orgPath}
            onChange={(e) => setOrgPath(e.target.value)}
            error={!queryOrgPath}
            helperText={queryOrgPath
              ? `Limited to ${rootOrgPath} and descendants`
              : `Enter ${rootOrgPath} or one of its descendants`}
          />
        </CardContent>
      </Card>

      {current.loading && <LinearProgress sx={{ mb: 2 }} />}
      {current.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {current.error}
        </Alert>
      )}

      {current.data && !current.loading && (
        <>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip label={`cohort: ${current.data.cohortSize}`} size="small" />
            <Chip label={`attempts: ${current.data.attempts}`} size="small" />
            <Chip
              label={`${current.data.entries.length} KSB tag${current.data.entries.length !== 1 ? 's' : ''}`}
              size="small"
            />
          </Box>

          {current.data.entries.length === 0 ? (
            <Alert severity="info">
              No scored attempts yet for this cohort — attainment cannot be computed.
            </Alert>
          ) : (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>KSB tag</TableCell>
                    <TableCell sx={{ width: 300 }}>Attainment</TableCell>
                    <TableCell sx={{ width: 80 }}>%</TableCell>
                    <TableCell sx={{ width: 90 }}>Students</TableCell>
                    <TableCell sx={{ width: 90 }}>Attempts</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {current.data.entries.map((row) => {
                    const color = colorForPct(row.attainment);
                    return (
                      <TableRow key={row.tag}>
                        <TableCell>
                          <Chip label={row.tag} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <LinearProgress
                            variant="determinate"
                            value={row.attainment}
                            color={color}
                            sx={{ height: 10, borderRadius: 5 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={`${color}.main`} fontWeight="bold">
                            {row.attainment}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {row.studentCount}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {row.sampleSize}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}

      {!current.data && !current.loading && !current.error && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Enter an org path above to see the cohort's KSB attainment.
        </Alert>
      )}
    </Box>
  );
}
